import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FaNewspaper, FaCalendarAlt } from "react-icons/fa";
import "./NewsEvents.css";
import { NEWS_API_URL } from "../config";

const API_BASE_URL = NEWS_API_URL;

const campuses = [
  { id: "Rajaji Nagar", label: "Rajajinagar" },
  { id: "Basveshwara Nagar", label: "Basaveshwaranagar" },
  { id: "Machohalli", label: "Machohalli" },
  { id: "Nagavruja", label: "Nagavruja" },
];

const typeFilters = [
  { id: "ALL", label: "All" },
  { id: "NEWS", label: "News" },
  { id: "EVENT", label: "Events" },
];

const formatDate = (isoString) =>
  new Date(isoString).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const NewsEvents = () => {
  const [activeCampus, setActiveCampus] = useState(campuses[0].id);
  const [activeType, setActiveType] = useState("ALL");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPosts = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { campus: activeCampus };
        if (activeType !== "ALL") params.type = activeType;

        const response = await axios.get(`${API_BASE_URL}/api/public/posts`, {
          params,
        });
        if (!cancelled) setPosts(response.data);
      } catch (err) {
        if (!cancelled) setError("Unable to load news & events right now. Please try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPosts();
    return () => {
      cancelled = true;
    };
  }, [activeCampus, activeType]);

  return (
    <div className="news-events-page">
      <section className="page-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>News &amp; Events</h1>
            <p>Stay updated with the latest happenings across Sri Vani campuses</p>
          </motion.div>
        </div>
      </section>

      <section className="news-events-section section">
        <div className="container">
          {/* Campus Filter */}
          <div className="news-events-filters">
            {campuses.map((campus) => (
              <button
                key={campus.id}
                className={`filter-btn ${activeCampus === campus.id ? "active" : ""}`}
                onClick={() => setActiveCampus(campus.id)}
              >
                {campus.label}
              </button>
            ))}
          </div>

          {/* Type Filter */}
          <div className="news-events-type-filters">
            {typeFilters.map((type) => (
              <button
                key={type.id}
                className={`type-filter-btn ${activeType === type.id ? "active" : ""}`}
                onClick={() => setActiveType(type.id)}
              >
                {type.label}
              </button>
            ))}
          </div>

          {loading && <p className="news-events-status">Loading…</p>}
          {!loading && error && <p className="news-events-status news-events-error">{error}</p>}
          {!loading && !error && posts.length === 0 && (
            <p className="news-events-status">No news or events found for this campus yet.</p>
          )}

          {!loading && !error && posts.length > 0 && (
            <div className="news-events-grid">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  className="news-event-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <div className="news-event-image">
                    {post.imageUrl ? (
                      <img src={post.imageUrl} alt={post.subject} />
                    ) : (
                      <div className="news-event-image-placeholder">
                        {post.type === "EVENT" ? <FaCalendarAlt /> : <FaNewspaper />}
                      </div>
                    )}
                    <span className={`news-event-badge news-event-badge--${post.type.toLowerCase()}`}>
                      {post.type === "EVENT" ? "Event" : "News"}
                    </span>
                  </div>
                  <div className="news-event-body">
                    <h3>{post.subject}</h3>
                    <p className="news-event-description">{post.description}</p>
                    <div className="news-event-meta">
                      <span>{post.campus}</span>
                      <span>{formatDate(post.createdAt)}</span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsEvents;
